
import { IMetaTag } from './meta-tag.interface';
import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { ISocialShare } from './social-share.interface';
import { ITag } from '@shared/interfaces/IArticle.interface';
import { Meta, Title } from '@angular/platform-browser';
import { environment } from '@env/environment';
import { hrefLangLinks, isUsBlog, stripPagingFromUrl, usContentRobots } from './seo-signals';

@Injectable({
  providedIn: 'root',
})
export class SocialShareService {

  constructor(
    @Inject(DOCUMENT) private readonly document: any,
    private readonly metaService: Meta,
    private readonly titleService: Title,
  ) { }

  setData(data: ISocialShare): void {
    this.setSection(data.section);
    this.setKeywords(data.keywords);
    this.setTitle(data.title);
    this.setType(data.type);
    this.setDescription(data.description);
    this.setImage(data.image);
    this.setUrl(data.url);
    this.setPublished(data.published);
    this.setModified(data.modified);
    this.setAuthor(data.author);
    this.setTags(data.hashes);
    this.setCanonicalUrl(data.url);
    this.setHrefLang(data.linkedArticleUrl, data.url, data.type, data.section, data.totalPages);
    this.setSiteName();
    this.setUsRobotsIfNeeded();
  }

  private setSiteName(): void {
    this.metaService.updateTag({ property: 'og:site_name', content: 'GoHenry' });
  }

  private setUsRobotsIfNeeded(): void {
    if (!isUsBlog(environment.country)) {
      return;
    }
    this.metaService.updateTag({ name: 'robots', content: usContentRobots() });
  }

  setKeywords(keywords: string): void {
    if (Boolean(keywords)) {
      this.metaService.updateTag({ name: 'keywords', content: keywords });
    } else {
      this.metaService.removeTag(`name='keywords'`);
    }
  }

  setSection(section?: string): void {
    if (Boolean(section)) {
      this.metaService.updateTag({ name: 'article:section', content: section });
    } else {
      this.metaService.removeTag(`name='article:section'`);
    }
  }

  setTitle(title: string = '') {
    this.titleService.setTitle(title);
    if (title && title.length) {
      this.metaService.updateTag({ property: 'twitter:title', content: title });
      this.metaService.updateTag({ property: 'twitter:image:alt', content: title });
      this.metaService.updateTag({ property: 'og:image:alt', content: title });
      this.metaService.updateTag({ property: 'og:title', content: title });
      this.metaService.updateTag({ name: 'title', content: title });
    } else {
      this.metaService.removeTag(`property='twitter:title'`);
      this.metaService.removeTag(`property='twitter:image:alt'`);
      this.metaService.removeTag(`property='og:image:alt'`);
      this.metaService.removeTag(`property='og:title'`);
      this.metaService.removeTag(`name='title'`);
    }
  }

  setType(type?: string) {
    if (type && type.length) {
      this.metaService.updateTag({ property: 'og:type', content: type });
    } else {
      this.metaService.removeTag(`property='og:type'`);
    }
  }

  setDescription(description?: string) {
    if (description && description.length) {
      this.metaService.updateTag({ property: 'twitter:description', content: description });
      this.metaService.updateTag({ property: 'og:description', content: description });
      this.metaService.updateTag({ name: 'description', content: description });
    } else {
      this.metaService.removeTag(`property='twitter:description'`);
      this.metaService.removeTag(`property='og:description'`);
      this.metaService.removeTag(`name='description'`);
    }
  }

  setImage(image?: string) {
    if (image && image.length) {
      this.metaService.updateTag({ property: 'twitter:image', content: image });
      this.metaService.updateTag({ property: 'og:image', content: image });
      this.metaService.updateTag({ property: 'og:image:height', content: '630' });
    } else {
      this.metaService.removeTag(`property='twitter:image'`);
      this.metaService.removeTag(`property='og:image'`);
      this.metaService.removeTag(`property='og:image:height'`);
    }
  }

  setUrl(url?: string) {
    if (url && url.length) {
      this.metaService.updateTag({ property: 'og:url', content: url });
      this.metaService.updateTag({ property: 'twitter:url', content: url });
    } else {
      this.metaService.removeTag(`property='og:url'`);
      this.metaService.removeTag(`property='twitter:url'`);
    }
  }

  setPublished(publishedDateString?: string) {
    if (publishedDateString) {
      let publishedDate: any = new Date(publishedDateString);
      // eslint-disable-next-line eqeqeq
      if (publishedDate == 'Invalid Date') {
        publishedDate = new Date();
      }
      this.metaService.updateTag({ name: 'article:published_time', content: publishedDate.toISOString() });
      this.metaService.updateTag({ name: 'published_date', content: publishedDate.toISOString() });
    } else {
      this.metaService.removeTag(`name='article:published_time'`);
      this.metaService.removeTag(`name='publication_date'`);
    }
  }

  setModified(modifiedDateString?: string) {
    if (modifiedDateString) {
      const modifiedDate = new Date(modifiedDateString);
      this.metaService.updateTag({ name: 'article:modified_time', content: modifiedDate.toISOString() });
      this.metaService.updateTag({ name: 'og:updated_time', content: modifiedDate.toISOString() });
    } else {
      this.metaService.removeTag(`name='article:modified_time'`);
      this.metaService.removeTag(`name='og:updated_time'`);
    }
  }

  setAuthor(author?: string) {
    if (author && author.length) {
      this.metaService.updateTag({ name: 'article:author', content: author });
      this.metaService.updateTag({ name: 'author', content: author });
    } else {
      this.metaService.removeTag(`name='article:author'`);
      this.metaService.removeTag(`name='author'`);
    }
  }

  setTwitterSiteCreator(site?: string): void {
    if (Boolean(site)) {
      this.metaService.updateTag({ property: 'twitter:site', content: site });
      this.metaService.updateTag({ property: 'twitter:creator', content: site });
    } else {
      this.metaService.removeTag(`property='twitter:site'`);
      this.metaService.removeTag(`property='twitter:creator'`);
    }
  }

  setTwitterCard(card?: string): void {
    if (Boolean(card)) {
      this.metaService.updateTag({ property: 'twitter:card', content: card });
    } else {
      this.metaService.removeTag(`property='twitter:card'`);
    }
  }

  setFbAppId(appId?: string): void {
    if (Boolean(appId)) {
      this.metaService.updateTag({ property: 'fb:app_id', content: appId });
    } else {
      this.metaService.removeTag(`property='fb:app_id'`);
    }
  }

  setTag(tag?: ITag) {
    if (tag) {
      this.metaService.addTag({ name: 'article:tag', content: tag.tag });
    }
  }

  setTags(metaTags: ITag[]): void {
    if (!metaTags) {
      return;
    }
    for (const metaTag of metaTags) {
      this.setTag(metaTag);
    }
  }

  setMetaTag(metaTag: IMetaTag): void {
    if (!metaTag) {
      return;
    }
    if (Boolean(metaTag.value)) {
      const metaTagObject = {
        [metaTag.attr]: metaTag.attrValue,
        content: metaTag.value,
      };
      this.metaService.updateTag(metaTagObject);
    } else {
      const selector = `${metaTag.attr}='${metaTag.attrValue}'`;
      this.metaService.removeTag(selector);
    }
  }

  setMetaTags(metaTags: IMetaTag[]): void {
    for (const metaTag of metaTags) {
      this.setMetaTag(metaTag);
    }
  }

  setCanonicalUrl(url?: string) {
    // first remove potential previous url
    const selector = `link[rel='canonical']`;
    const canonicalElement = this.document.head.querySelector(selector);
    const cleanedURL = stripPagingFromUrl(url);
    if (canonicalElement) {
      this.document.head.removeChild(canonicalElement);
    }

    if (url && url.length) {
      const link: HTMLLinkElement = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
      link.setAttribute('href', cleanedURL);
    }
  }

  public setHrefLang(linkedArticleUrl?: string, url?: string, type?: string, section?: string, _totalPages?: Array<number>) {
    const selector = `link[rel='alternate']`;
    const altElements = this.document.head.querySelectorAll(selector);

    if (altElements.length > 0) {
      for (let index = 0; index < altElements.length; index++) {
        this.document.head.removeChild(altElements[index]);
      }
    }

    const links = hrefLangLinks({
      locale: environment.locale,
      url,
      type,
      linkedArticleUrl,
      section,
    });

    for (const item of links) {
      const link: HTMLLinkElement = this.document.createElement('link');
      link.setAttribute('rel', 'alternate');
      this.document.head.appendChild(link);
      link.setAttribute('href', item.href);
      link.setAttribute('hreflang', item.hreflang);
    }
  }

}
